import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { motion } from "framer-motion";
import { secondsToHms } from "../../utils";
import styles from "./mediaChapter.module.scss";

interface AddedChapterThumbnailProps {
  thumbnailUrl: string | null;
  title: string;
  description: string;
  duration: number;
  startTime?: number;
  endTime?: number;
  isGenerated?: boolean;
  id: string;
  handleDelete: (id: string) => void;
  handleEdit: (id: string) => void;
  handleGenerate?: (id: string) => Promise<unknown> | void;
  isSelected: boolean;
}

const AddedChapterThumbnail: React.FC<AddedChapterThumbnailProps> = ({
  thumbnailUrl,
  title,
  description,
  duration,
  isGenerated = false,
  handleEdit,
  handleDelete,
  handleGenerate,
  isSelected,
  id,
}) => {
  const [optionOpen, setOptionOpen] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const moreOptionsTriggerRef = useRef<HTMLElement>(null);
  const moreOptionsMenuRef = useRef<HTMLDivElement>(null);

  const onGenerateClick = async () => {
    if (isGenerating || !handleGenerate) return;
    setIsGenerating(true);
    try {
      await handleGenerate(id);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    if (
      moreOptionsTriggerRef.current?.contains(target) ||
      moreOptionsMenuRef.current?.contains(target)
    ) {
      return;
    }
    setOptionOpen(false);
  };

  useEffect(() => {
    if (optionOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [optionOpen]);

  // The card's own `overflow: hidden` (needed for its rounded corners and
  // the exit-animation height collapse) clips this menu if it stays a plain
  // DOM child - it must render above the card via `bottom: 100%`-style
  // positioning, which the card's own bounds cut off. Portaling to
  // document.body and positioning from the trigger icon's real screen
  // coordinates escapes that clipping the same way RenderAction.tsx's
  // more-options menu already does for the media list.
  const updateMenuPosition = useCallback(() => {
    const trigger = moreOptionsTriggerRef.current;
    const menu = moreOptionsMenuRef.current;
    if (!trigger || !menu) return;
    const triggerRect = trigger.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = triggerRect.top + window.scrollY - menuRect.height - 4;
    // Not enough room above (e.g. the first row of cards) - drop below
    // the trigger instead of rendering off the top of the viewport.
    if (top < window.scrollY) {
      top = triggerRect.bottom + window.scrollY + 4;
    }
    let left = triggerRect.right + window.scrollX - menuRect.width;
    if (left < window.scrollX) left = window.scrollX + 4;
    if (left + menuRect.width > viewportWidth + window.scrollX) {
      left = viewportWidth + window.scrollX - menuRect.width - 4;
    }
    if (top + menuRect.height > viewportHeight + window.scrollY) {
      top = viewportHeight + window.scrollY - menuRect.height - 4;
    }

    setMenuPosition((prev) =>
      prev.top === top && prev.left === left ? prev : { top, left }
    );
  }, []);

  useEffect(() => {
    if (!optionOpen) return;
    const timeoutId = window.setTimeout(updateMenuPosition, 0);
    window.addEventListener("scroll", updateMenuPosition, true);
    window.addEventListener("resize", updateMenuPosition);
    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
    };
  }, [optionOpen, updateMenuPosition]);

  return (
    <motion.div
      className={`card d-flex flex-row ${styles["budget-card"]} ${
        isSelected && "border border-primary"
      } `}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0 }}
      style={{ maxWidth: "100%", padding: 0, overflow: "hidden" }}
    >
      {thumbnailUrl ? (
        <div
          className="position-relative flex-shrink-0"
          onClick={() => {
            handleEdit(id);
          }}
          style={{
            width: "150px",
            aspectRatio: "16 / 9",
            overflow: "hidden",
            borderRadius: "4px 0 0 4px",
          }}
        >
          {id === "new_chapter" && (
            <span
              className="position-absolute bg-danger text-white px-2 py-1 top-0 start-0 small z-3"
              style={{
                height: "20px",
                width: "40px",
                display: "flex",
                alignItems: "center",
                fontSize: "10px",

                justifyContent: "center",
              }}
            >
              NEW
            </span>
          )}
          <img
            src={thumbnailUrl}
            alt="Video thumbnail"
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              zIndex: 1,
              objectFit: "cover",
            }}
          />
          <div>
            <span
              className={`position-absolute bg-dark bg-opacity-75 text-white px-2 py-1 small ${styles["video-duration"]}`}
              style={{ zIndex: 2 }}
            >
              {secondsToHms(duration)}
            </span>
          </div>
          {id !== "new_chapter" && (
            <div>
              <span
                className="position-absolute rounded-pill"
                style={{
                  zIndex: 2,
                  width: "auto",
                  height: "16px",
                  lineHeight: "16px",
                  bottom: "8px",
                  left: "8px",
                  padding: "0 6px",
                  fontSize: "9px",
                  fontWeight: 700,
                  textAlign: "center",
                  letterSpacing: "0.03em",
                  color: isGenerated ? "#1a7f37" : "#6c757d",
                  background: isGenerated ? "#d7f5e0" : "#e9ecef",
                }}
              >
                {isGenerated ? "GENERATED" : "DRAFT"}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center flex-shrink-0"
          style={{
            width: "150px",
            aspectRatio: "16 / 9",
            borderRadius: "4px 0 0 4px",
            background: "#f1f3f5",
          }}
        >
          Generating thumbnail
        </div>
      )}
      <div
        className="card-body d-flex flex-column"
        style={{
          borderRadius: "0 4px 4px 0",
          minWidth: 0,
          padding: "10px 12px",
        }}
      >
        <div
          className="d-flex justify-content-between align-items-center gap-2"
          style={{ minWidth: 0 }}
        >
          <span
            className="text-truncate fw-medium f-14 d-block"
            style={{ minWidth: 0 }}
          >
            {title}
          </span>
        </div>

        {description && (
          <div
            className={`text-muted text-truncate f-12 mt-1 ${styles["clip-description"]}`}
            title={description}
          >
            {description}
          </div>
        )}

        {id !== "new_chapter" && (
          <div className="d-flex justify-content-end align-items-center gap-2 border-top mt-2 pt-2 flex-grow-1 position-relative">
            {isGenerated ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(id);
                }}
              >
                Edit
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary generate-thumbnail-btn d-flex align-items-center gap-1"
                disabled={isGenerating}
                onClick={(e) => {
                  e.stopPropagation();
                  onGenerateClick();
                }}
              >
                {isGenerating && (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                )}
                {isGenerating ? "Generating..." : "Generate"}
              </button>
            )}
            <i
              ref={moreOptionsTriggerRef as React.RefObject<HTMLElement>}
              className="bi bi-three-dots-vertical pointer fw-bold flex-shrink-0 f-14"
              onClick={(e) => {
                e.stopPropagation();
                setOptionOpen((state) => !state);
              }}
            ></i>
            {optionOpen &&
              createPortal(
                <div
                  ref={moreOptionsMenuRef}
                  className="list-group rounded-1"
                  style={{
                    position: "absolute",
                    top: menuPosition.top,
                    left: menuPosition.left,
                    zIndex: 1050,
                    minWidth: "100px",
                    boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
                  }}
                >
                  <li
                    className="list-group-item pointer d-flex justify-content-start align-items-center gap-2"
                    onClick={() => {
                      handleEdit(id);
                      setOptionOpen(false);
                    }}
                  >
                    <i className="bi bi-pencil-square"></i>Edit
                  </li>
                  <li
                    className="list-group-item pointer d-flex justify-content-start align-items-center gap-2"
                    onClick={() => {
                      handleDelete(id);
                      setOptionOpen(false);
                    }}
                  >
                    <i className="bi bi-trash"></i>Delete
                  </li>
                </div>,
                document.body
              )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AddedChapterThumbnail;
